import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { Menu, X, Instagram } from 'lucide-react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../contexts/LanguageContext';
import { LANGUAGE_ROUTE_PAIRS } from '../constants';
import { ARTICLES_OVERVIEW, isArticlesPath } from '../articles';

export const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const nt = t.nav;

  const handleLanguageChange = (lang: 'EN' | 'DE') => {
    setLanguage(lang);
    const pair = LANGUAGE_ROUTE_PAIRS[location.pathname];
    if (pair && pair[lang] !== location.pathname) {
      navigate(pair[lang]);
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      const firstLink = document.querySelector('#mobile-menu a') as HTMLElement;
      firstLink?.focus();
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileMenuOpen]);

  // These are same-page anchors on Home, reached from anywhere on the site —
  // must carry the current language's Home prefix (/de/ or /en/), not the
  // bare "/", which no longer has a route and would otherwise always land
  // on CatchAllRedirect's German default regardless of the visitor's actual
  // language (see App.tsx's CatchAllRedirect).
  const homePrefix = language === 'EN' ? '/en/' : '/de/';
  const navLinks = [
    { name: nt.why, href: `${homePrefix}#why-us` },
    { name: nt.fit, href: `${homePrefix}#fit-check` },
    { name: nt.pricing, href: `${homePrefix}#pricing` },
    { name: nt.team, href: `${homePrefix}#team` },
    { name: nt.calculator, href: `${homePrefix}#calculator` },
    { name: nt.faq, href: `${homePrefix}#faq` },
    // Own page, not a Home anchor: active on the overview, every article and
    // every click-through.
    {
      name: nt.articles,
      href: ARTICLES_OVERVIEW[language === 'EN' ? 'en' : 'de'].path,
      active: isArticlesPath(location.pathname),
    },
  ];

  const LanguageToggle = () => (
    <div className="flex items-center gap-1 bg-pstax-bg/50 p-1 rounded-full border border-pstax-border">
      {(['EN', 'DE'] as const).map((lang) => (
        <button
          key={lang}
          onClick={() => handleLanguageChange(lang)}
          className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-all ${
            language === lang 
              ? 'bg-pstax-blue text-pstax-navy shadow-sm' 
              : 'text-pstax-navy/50 hover:text-pstax-navy'
          }`}
        >
          {lang}
        </button>
      ))}
    </div>
  );

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'py-3 bg-white/95 border-b border-pstax-border shadow-sm' : 'py-4 bg-transparent'}`}>
      <div className="container mx-auto px-6 flex items-center justify-between gap-6 xl:gap-12">
        <Link to={language === 'EN' ? '/en/' : '/de/'} className="flex items-center gap-2 group shrink-0" aria-label="PSTax Home">
          <img 
            src="/regenerated_image_1777641628772.webp" 
            alt="PSTax logo" 
            className="h-8 w-auto group-hover:scale-105 transition-transform"
            decoding="async"
          />
          <span className="text-xl font-display font-bold tracking-tighter text-pstax-navy">PSTax</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-6">
          <div className="flex items-center gap-3 xl:gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                aria-current={link.active ? 'page' : undefined}
                className={`text-[14px] transition-colors whitespace-nowrap ${
                  link.active ? 'font-semibold text-pstax-navy' : 'font-medium text-pstax-navy/70 hover:text-pstax-navy'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to={`${homePrefix}#fit-check`} className="btn-primary text-[14px] py-2.5 px-6 whitespace-nowrap" data-cta-location="navigation">
              {nt.cta}
            </Link>
            <a
              href="https://www.instagram.com/pascal_pstax/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="PSTax on Instagram"
              className="hidden xl:block text-pstax-navy/70 hover:text-pstax-navy transition-colors shrink-0"
            >
              <Instagram size={18} />
            </a>
          </div>
        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-4 lg:hidden">
          <LanguageToggle />
          <button 
            className="text-pstax-navy"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <m.div 
            id="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 right-0 bg-white border-b border-pstax-border lg:hidden shadow-lg"
          >
            <div className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <Link 
                  key={link.name} 
                  to={link.href} 
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-current={link.active ? 'page' : undefined}
                  className={`text-lg ${
                    link.active ? 'font-semibold text-pstax-navy' : 'font-medium text-pstax-navy/70 hover:text-pstax-navy'
                  }`}
                  data-cta-location="navigation"
                >
                  {link.name}
                </Link>
              ))}
              
              <hr className="border-pstax-border" />
              <Link
                to={`${homePrefix}#fit-check`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="btn-primary text-center"
                data-cta-location="navigation"
              >
                {nt.cta}
              </Link>
              <a
                href="https://www.instagram.com/pascal_pstax/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PSTax on Instagram"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 text-pstax-navy/70 hover:text-pstax-navy transition-colors py-3"
              >
                <Instagram size={20} />
              </a>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
